function pickMimeType() {
    if (typeof window === 'undefined' || typeof window.MediaRecorder === 'undefined') return ''
    const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus']
    const selected = candidates.find(item => {
        try {
            return window.MediaRecorder.isTypeSupported(item)
        } catch {
            return false
        }
    })
    return selected || ''
}

export function createCallRecordingState() {
    return {
        enabledByPolicy: false,
        startedAt: null,
        mediaRecorder: null,
        mimeType: '',
        chunks: [],
        audioContext: null,
        destination: null,
        streamSources: {},
        isUploading: false,
    }
}

export function setCallRecordingPolicy(state, enabled) {
    if (!state) return
    state.enabledByPolicy = enabled === true
}

export function canRecordCall(state) {
    if (!state?.enabledByPolicy) return false
    if (typeof window === 'undefined') return false
    if (typeof window.MediaRecorder === 'undefined') return false
    if (typeof window.AudioContext === 'undefined' && typeof window.webkitAudioContext === 'undefined') return false
    return true
}

function ensureAudioGraph(state) {
    if (state.audioContext && state.destination) return
    const AudioContextCtor = window.AudioContext || window.webkitAudioContext
    state.audioContext = new AudioContextCtor()
    state.destination = state.audioContext.createMediaStreamDestination()
}

export function attachStreamToRecording(state, stream, key) {
    if (!canRecordCall(state)) return false
    if (!stream || !key) return false

    ensureAudioGraph(state)
    if (state.streamSources[key]) return true

    const tracks = typeof stream.getAudioTracks === 'function' ? stream.getAudioTracks() : []
    if (!Array.isArray(tracks) || tracks.length === 0) return false

    const sourceNode = state.audioContext.createMediaStreamSource(stream)
    sourceNode.connect(state.destination)
    state.streamSources[key] = sourceNode
    return true
}

export function startCallRecording(state) {
    if (!canRecordCall(state)) return false
    if (state.mediaRecorder || !state.destination) return false

    const destinationTracks = state.destination.stream?.getAudioTracks?.() || []
    if (!destinationTracks.length) return false

    const mimeType = pickMimeType()
    const recorderOptions = mimeType ? { mimeType } : undefined
    state.mediaRecorder = new window.MediaRecorder(state.destination.stream, recorderOptions)
    state.mimeType = mimeType || 'audio/webm'
    state.chunks = []
    state.startedAt = new Date().toISOString()

    state.mediaRecorder.ondataavailable = event => {
        if (event?.data && event.data.size > 0) {
            state.chunks.push(event.data)
        }
    }

    state.mediaRecorder.start(1000)
    return true
}

function blobToDataUrl(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(String(reader.result || ''))
        reader.onerror = reject
        reader.readAsDataURL(blob)
    })
}

export async function stopCallRecordingAndUpload(state, socket, payloadBase) {
    if (!state?.mediaRecorder || state.isUploading) return { ok: false, error: 'NO_ACTIVE_RECORDING' }

    state.isUploading = true
    const recorder = state.mediaRecorder

    const stopResult = await new Promise(resolve => {
        recorder.onstop = async () => {
            try {
                const blob = new Blob(state.chunks, { type: state.mimeType || 'audio/webm' })
                if (!blob || blob.size <= 0) {
                    resolve({ ok: false, error: 'EMPTY_RECORDING' })
                    return
                }

                const audioBase64 = await blobToDataUrl(blob)
                const endedAt = new Date().toISOString()
                const durationSec = payloadBase?.startedAt ? Math.max(0, Math.round((new Date(endedAt).getTime() - new Date(payloadBase.startedAt).getTime()) / 1000)) : 0

                socket.emit(
                    'call:recording:upload',
                    {
                        ...payloadBase,
                        startedAt: payloadBase?.startedAt || state.startedAt,
                        endedAt,
                        durationSec,
                        mimeType: state.mimeType || 'audio/webm',
                        audioBase64,
                    },
                    ack => {
                        if (ack?.ok) {
                            resolve({ ok: true, ack })
                            return
                        }
                        resolve({ ok: false, error: ack?.error || 'UPLOAD_FAILED' })
                    },
                )
            } catch (error) {
                resolve({ ok: false, error: String(error?.message || error || 'UPLOAD_FAILED') })
            }
        }

        recorder.onerror = event => {
            resolve({ ok: false, error: String(event?.error?.message || 'RECORDER_ERROR') })
        }

        try {
            recorder.stop()
        } catch (error) {
            resolve({ ok: false, error: String(error?.message || error || 'RECORDER_STOP_FAILED') })
        }
    })

    resetCallRecordingState(state)
    return stopResult
}

export function resetCallRecordingState(state) {
    if (!state) return

    Object.keys(state.streamSources || {}).forEach(key => {
        const source = state.streamSources[key]
        try {
            source?.disconnect?.()
        } catch {
            // noop
        }
    })

    try {
        state.destination?.disconnect?.()
    } catch {
        // noop
    }

    try {
        state.audioContext?.close?.()
    } catch {
        // noop
    }

    state.mediaRecorder = null
    state.mimeType = ''
    state.chunks = []
    state.startedAt = null
    state.audioContext = null
    state.destination = null
    state.streamSources = {}
    state.isUploading = false
}
